import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';

import ProtectedRoute from './components/ProtectedRoute';
import ChatWindow from './components/ChatWindow';
import ContactsSidebar from './components/ContactsSidebar';
import ChatScreen from './screens/ChatScreen';
import ForumHome from './screens/forum/ForumHome';
import ForumCategory from './screens/forum/ForumCategory';
import ForumNewThread from './screens/forum/ForumNewThread';
import ForumThreadView from './screens/forum/ForumThreadView';
import ForumThreadDetail from './screens/forum/ForumThreadDetail';
import ProfilePage from './pages/ProfilePage';
import NatalChartScreen from './screens/NatalChartScreen';
import { authenticatedRequest } from './services/authApi';
import { useTranslation } from 'react-i18next';
import { normalizeHoroscopeLocale } from './utils/horoscopeLocale';
import { localizedValue } from './localization/presentationLanguage';

// Screens
import WelcomeScreen from './screens/WelcomeScreen';
import RegisterScreen from './screens/RegisterScreen';
import LoginScreen from './screens/LoginScreen';
import PrivacyPolicy from './screens/PrivacyPolicy';
import Terms from './screens/Terms';
import HomeScreen from './screens/HomeScreen';
import ZodiacRoomsScreen from './screens/ZodiacRoomsScreen';
import RoomScreen from './screens/RoomScreen';
import ProfileScreen from './screens/ProfileScreen';
import ZodiacRouletteRoom from './screens/ZodiacRouletteRoom';
import SignInfoIndex from './screens/SignInfoIndex';
import ZodiacInfoScreen from './screens/ZodiacInfoScreen';
import FilteredRouletteRoom from './screens/FilteredRouletteRoom';
import VerifyEmail from './screens/VerifyEmail';
import { openOrCreateConversation } from './services/chatApi';
import { getAuthToken } from './utils/auth';
import RouletteHubScreen from './screens/RouletteHubScreen';
import SupportScreen from './screens/SupportScreen';
import ZodiacClashScreen from './screens/zodiacclash/ZodiacClashScreen';
import ZodiacCrushScreen from './games/zodiacCrush/ui/ZodiacCrushScreen';
import ZodiacCrushMapScreen from './games/zodiacCrush/ui/ZodiacCrushMapScreen';
import SynastryRoomScreen from './screens/SynastryRoom/SynastryRoomScreen';
import SavedSynastriesScreen from './screens/SynastryRoom/SavedSynastriesScreen';
import SynastryComparisonScreen from './screens/SynastryComparisonScreen';
import FeedScreen from './screens/FeedScreen';
import AdminFeedReportsScreen from './screens/admin/AdminFeedReportsScreen';
import ToastProvider from './context/ToastProvider';
import { useAuth } from './context/AuthContext';
import AuthenticatedSocketLifecycle from './components/AuthenticatedSocketLifecycle';
import { REST_API_ORIGIN, SOCKET_IO_ORIGIN } from './platform/endpoints';
import PlatformRuntime from './platform/PlatformRuntime';

// 👇 IMPORTA ESTAS 3 PANTALLAS DE AUTH
import ForgotPassword from './screens/ForgotPassword';
import ResetPassword from './screens/ResetPassword';
import ChangePassword from './screens/ChangePassword';
import AccountDeletionResource from './screens/AccountDeletionResource';
import ChildSafetyStandards from './screens/ChildSafetyStandards';
import NotificationPreferences from './screens/NotificationPreferences';
import OracleScreen from './screens/OracleScreen';
import OracleWeeklyScreen from './screens/OracleWeeklyScreen';


const uiText = (es, en) => localizedValue({ es, en });

const pickApiBase = () => REST_API_ORIGIN;
const pickSocketBase = () => SOCKET_IO_ORIGIN;

// === Debug/Monitor badge: API + Socket status (visible en dev u on-demand) ===
function StatusBadge() {
  const [apiOk, setApiOk] = React.useState(null);
  const [wsOk, setWsOk] = React.useState(null);

  React.useEffect(() => {
    let closed = false;

    // 1) API health (direct to backend — avoid relying on Vercel rewrites)
    (() => {
      const apiBase = pickApiBase();
      fetch(`${apiBase}/health`, { cache: 'no-store' })
        .then(r => (r.ok ? r.json() : Promise.reject()))
        .then(() => { if (!closed) setApiOk(true); })
        .catch(() => { if (!closed) setApiOk(false); });
    })();

    // 2) Socket reachability (NO real socket connect to avoid noisy extra sessions in dev)
    (() => {
      const base = pickSocketBase();
      // Socket.IO polling endpoint; a 200-ish response means the socket endpoint is reachable.
      // We intentionally avoid opening an actual websocket client here.
      const url = `${base}/socket.io/?EIO=4&transport=polling&t=${Date.now()}`;
      fetch(url, { method: 'GET', cache: 'no-store' })
        .then(r => {
          if (!closed) setWsOk(!!r && (r.ok || (r.status >= 200 && r.status < 500)));
        })
        .catch(() => { if (!closed) setWsOk(false); });
    })();

    return () => { closed = true; };
  }, []);

  const dot = (ok) => ({
    display: 'inline-block',
    width: 10, height: 10, borderRadius: '50%',
    background: ok == null ? '#bdbdbd' : ok ? '#2ecc71' : '#e74c3c',
    boxShadow: '0 0 6px rgba(0,0,0,0.35)',
    marginLeft: 6
  });

  return (
    <div style={{
      position: 'fixed', top: 8, right: 8, zIndex: 9999,
      background: 'rgba(0,0,0,0.55)', color:'#fff',
      border: '1px solid rgba(255,255,255,0.25)',
      borderRadius: 12, padding: '6px 8px',
      fontSize: 12, display: 'flex', gap: 10, alignItems: 'center',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
    }}>
      <span>API <i style={dot(apiOk)} /></span>
      <span>WS <i style={dot(wsOk)} /></span>
    </div>
  );
}
// === /StatusBadge ===

// === Version sync banner: checks backend /healthz and compares with FE build ===
const EXPECTED_API_VERSION = process.env.REACT_APP_API_VERSION || null;
const API_BASE = REST_API_ORIGIN;

function VersionSync() {
  const [mismatch, setMismatch] = React.useState(false);
  const [backendVersion, setBackendVersion] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!API_BASE || !EXPECTED_API_VERSION) return; // nothing to compare
      try {
        const r = await fetch(`${API_BASE}/healthz`, { cache: 'no-store' });
        const j = await r.json();
        if (cancelled) return;
        const got = j?.version || null;
        setBackendVersion(got);
        if (got && got !== EXPECTED_API_VERSION) {
          setMismatch(true);
        }
      } catch {}
    };
    check();
    // También re-chequea luego de unos segundos por si el BE termina de levantar
    const t = setTimeout(check, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  if (!mismatch) return null;
  return (
    <div style={{
      position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)',
      zIndex: 2000, padding: '8px 12px', borderRadius: 10,
      background: 'linear-gradient(135deg, #ffef9a, #ffd54f)', color: '#4B0082',
      border: '1px solid rgba(218,165,32,0.6)', boxShadow: '0 8px 18px rgba(0,0,0,0.25)'
    }} role="status" aria-live="polite">
      <b>{uiText('Actualización disponible', 'Update available')}</b> · Backend {backendVersion || uiText('desconocido', 'unknown')} ≠ Frontend {EXPECTED_API_VERSION}.{' '}
      <button
        onClick={() => window.location.reload(true)}
        style={{ marginLeft: 8, padding: '6px 10px', borderRadius: 8, border: '1px solid #4B008244', cursor: 'pointer' }}
      >
        {uiText('Recargar', 'Reload')}
      </button>
    </div>
  );
}

function DirectMessageRoute() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  // Legacy deep links now use the same authenticated, persisted conversation
  // path as the contacts UI.  They must never fall back to generic dm:* rooms.
  useEffect(() => {
    let cancelled = false;
    const friendId = String(id || '').trim();
    if (!/^[a-f0-9]{24}$/i.test(friendId)) {
      setFailed(true);
      return () => { cancelled = true; };
    }
    const token = getAuthToken() || undefined;

    openOrCreateConversation(token, friendId)
      .then((conversation) => {
        const conversationId = conversation?._id || conversation?.id;
        if (!conversationId) throw new Error('conversation_not_available');
        if (!cancelled) navigate(`/chat/${conversationId}`, { replace: true, state: { peerUserId: friendId } });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => { cancelled = true; };
  }, [id, navigate]);

  if (failed) return <Navigate to="/contacts" replace />;
  return <div role="status">{uiText('Abriendo conversación…', 'Opening conversation…')}</div>;
}

function safeParseStoredJson(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : decodeURIComponent(
          Array.prototype.map
            .call(Buffer.from(padded, 'base64').toString('binary'), (c) =>
              `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
            )
            .join('')
        );
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function isTruthyAdminFlag(value) {
  if (value === true) return true;
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

function normalizeRoleList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return [];
  return normalized
    .split(/[\s,|/]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function extractAdminCandidates(source) {
  if (!source || typeof source !== 'object') return [];
  const nestedUser = source.user && typeof source.user === 'object' ? source.user : null;
  const nestedProfile = source.profile && typeof source.profile === 'object' ? source.profile : null;
  const nestedData = source.data && typeof source.data === 'object' ? source.data : null;
  return [source, nestedUser, nestedProfile, nestedData].filter(Boolean);
}

function canAccessAdminFeedReports() {
  try {
    const storedObjects = [
      safeParseStoredJson(localStorage.getItem('user')),
      safeParseStoredJson(localStorage.getItem('userInfo')),
      safeParseStoredJson(localStorage.getItem('authUser')),
      safeParseStoredJson(sessionStorage.getItem('user')),
      safeParseStoredJson(sessionStorage.getItem('userInfo')),
      safeParseStoredJson(sessionStorage.getItem('authUser')),
    ].filter(Boolean);

    const tokenCandidates = [getAuthToken()].filter(Boolean);

    const decodedTokens = tokenCandidates
      .map((token) => decodeJwtPayload(token))
      .filter(Boolean);

    const candidates = [...storedObjects, ...decodedTokens]
      .flatMap((entry) => extractAdminCandidates(entry));

    for (const candidate of candidates) {
      const roleValues = [
        ...normalizeRoleList(candidate?.role),
        ...normalizeRoleList(candidate?.userRole),
        ...normalizeRoleList(candidate?.roles),
        ...normalizeRoleList(candidate?.permissions),
      ];

      if (isTruthyAdminFlag(candidate?.isAdmin)) return true;
      if (isTruthyAdminFlag(candidate?.admin)) return true;
      if (isTruthyAdminFlag(candidate?.isSuperAdmin)) return true;
      if (roleValues.some((role) => ['admin', 'gm', 'owner', 'superadmin'].includes(role))) return true;
      if (roleValues.some((role) => role.includes('admin'))) return true;
    }

    return false;
  } catch {
    return false;
  }
}

function App() {
  const { token, user, isAuthReady } = useAuth();
  const { i18n } = useTranslation();
  const horoscopeLocale = normalizeHoroscopeLocale(i18n.resolvedLanguage || i18n.language);

  // === Prefetch today's horoscope for instant Home render ===
  React.useEffect(() => {
    try {
      if (!isAuthReady || !user || !token) return;

      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const protectedPrefetchPaths = [
        '/home',
        '/profile',
        '/rooms',
        '/zodiac-clash',
        '/zodiac-crush',
        '/synastry',
        '/saved-synastries',
      ];

      const shouldPrefetch = protectedPrefetchPaths.some(
        (basePath) => pathname === basePath || pathname.startsWith(`${basePath}/`)
      );

      if (!shouldPrefetch) return;

      // Avoid refetch if already prefetched in this session
      if (window.__HORO_PREFETCH?.locale === horoscopeLocale) return;

      const apiBase = pickApiBase();
      authenticatedRequest(`${apiBase}/api/horoscope/today`, {
        rawResponse: true,
        credentials: 'include',
        headers: {
          
          'Accept-Language': horoscopeLocale,
        },
        diagSource: 'App.horoscopePrefetch',
      })
        .then(async (r) => {
          if (r.status === 401 || r.status === 403) return null;
          return r.ok ? r.json() : null;
        })
        .then((data) => {
          if (data && data.ok) {
            window.__HORO_PREFETCH = data;
          }
        })
        .catch(() => {});
    } catch {}
  }, [isAuthReady, user, token, horoscopeLocale]);

  const showStatusBadge = process.env.NODE_ENV !== 'production'
    || (typeof localStorage !== 'undefined' && localStorage.getItem('zdx:status') === '1');
  return (
      <ToastProvider>
        <AuthenticatedSocketLifecycle />
        {showStatusBadge ? <StatusBadge /> : null}
        <Router>
          <PlatformRuntime />
          <VersionSync />
          <Routes>
            {/* públicas */}
            <Route path="/" element={<WelcomeScreen />} />
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/child-safety" element={<ChildSafetyStandards />} />
            <Route path="/terminos" element={<Terms />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* 🔓 públicas para recuperación */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* protegidas */}
            <Route path="/foro" element={<ForumHome />} />
            <Route path="/foro/:sign" element={<ForumCategory />} />
            <Route path="/foro/:sign/nuevo" element={<ForumNewThread />} />
            <Route path="/foro/tema/:slug" element={<ForumThreadView />} />
            <Route path="/foro/thread/:id" element={<ForumThreadDetail />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomeScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zodiac-clash"
              element={
                <ProtectedRoute>
                  <ZodiacClashScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zodiac-crush"
              element={
                <ProtectedRoute>
                  <ZodiacCrushMapScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zodiac-crush/map"
              element={
                <ProtectedRoute>
                  <ZodiacCrushMapScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zodiac-crush/play"
              element={
                <ProtectedRoute>
                  <ZodiacCrushScreen />
                </ProtectedRoute>
              }
            />
            {process.env.REACT_APP_ORACLE_ENABLED === '1' ? (
              <>
                <Route path="/oracle" element={<ProtectedRoute><OracleScreen /></ProtectedRoute>} />
                <Route path="/oracle/weekly" element={<ProtectedRoute><OracleWeeklyScreen /></ProtectedRoute>} />
              </>
            ) : null}
            <Route
              path="/synastry"
              element={
                <ProtectedRoute>
                  <SynastryRoomScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sinastry"
              element={<Navigate to="/synastry" replace />}
            />
            <Route
              path="/saved-synastries"
              element={
                <ProtectedRoute>
                  <SavedSynastriesScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed"
              element={
                <ProtectedRoute>
                  <FeedScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feed/reports"
              element={
                <ProtectedRoute>
                  {canAccessAdminFeedReports() ? (
                    <AdminFeedReportsScreen />
                  ) : (
                    <Navigate to="/feed" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/synastry-comparison"
              element={
                <ProtectedRoute>
                  <SynastryComparisonScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sign-info"
              element={
                <ProtectedRoute>
                  <SignInfoIndex />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sign-info/:sign"
              element={
                <ProtectedRoute>
                  <ZodiacInfoScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/rooms"
              element={
                <ProtectedRoute>
                  <ZodiacRoomsScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rooms/:signo"
              element={
                <ProtectedRoute>
                  <RoomScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ruletas"
              element={
                <ProtectedRoute>
                  <RouletteHubScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/zodiac-roulette"
              element={
                <ProtectedRoute>
                  <ZodiacRouletteRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/filtered-roulette/:mode"
              element={
                <ProtectedRoute>
                  <FilteredRouletteRoom />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfileScreen />
                </ProtectedRoute>
              }
            />
            <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
            <Route path="/delete-account" element={<AccountDeletionResource />} />

            <Route
              path="/contacts"
              element={
                <ProtectedRoute>
                  <ContactsSidebar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dm/:id"
              element={
                <ProtectedRoute>
                  <DirectMessageRoute />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat/:conversationId"
              element={
                <ProtectedRoute>
                  <ChatWindow />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportScreen />
                </ProtectedRoute>
              }
            />
          {/* NATAL */}
          <Route
            path="/carta-astral"
            element={
              <ProtectedRoute>
                <NatalChartScreen />
              </ProtectedRoute>
            }
          />

            {/* opcional: pantalla de chat “general” */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatScreen />
                </ProtectedRoute>
              }
            />

            {/* protegida: cambio de contraseña */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
          
        <Route
          path="/natal-chart"
          element={
            <ProtectedRoute>
              <NatalChartScreen />
            </ProtectedRoute>
          }
        />
</Routes>
        </Router>
      </ToastProvider>
  );
}

export default App;
